"use client";

import moment from "moment-timezone";
import Link from "next/link";
import { FC, useEffect } from "react";
import { IoMdHome } from "react-icons/io";

import { LexicalRichText } from "@/components/LexicalRichText";
import SocialShare from "@/components/shared/SocialShare";
import { Typography } from "@/components/shared/Typography";
import WhatsappTelegramChannel from "@/components/shared/WhatsappTelegramChannel";
import { PodcastType } from "@/types/podcasts";

interface PodcastDetailHeroSectionProps {
  article: PodcastType;
  lang: string;
}

const PodcastDetailHeroSection: FC<PodcastDetailHeroSectionProps> = ({ article, lang }) => {

  useEffect(() => {
    const targetElement = document.getElementById("embed-wrapper");
    if (targetElement) {
      targetElement.innerHTML = article.spotifyEmbedUrl;
    }
  }, [article]);

  return (
    <div className="w-full ">
      <div className="flex items-center gap-2 pb-2 divide-x">
        <Link href={`/`} aria-label="Go to home page">
          <p className="!text-[#8A8A8A] text-lg">
            <IoMdHome />
          </p>
        </Link>
        <Link href={`/podcasts`} aria-label="Go to podcasts page">
          <Typography language="ml" variant="body1" color="#8A8A8A" className="pl-2">
            Podcast
          </Typography>
        </Link>
      </div>

      <h1 className="!text-[20px] lg:!text-[29px] leading-[1.5] md:leading-[1.5] font-[600] font-notosans dark:text-white">
        {article?.title}
      </h1>

      <div className="mt-4">
        <div className="relative w-full overflow-hidden" id="embed-wrapper"></div>
      </div>

      <div className="flex flex-col items-center justify-between gap-4 py-4 md:flex-row">
        <div className="flex items-center gap-4">
          <div className="">
            <Typography className="font-semibold" variant="subtitle2">
            {lang === "ml" ? "വെബ് ഡെസ്ക്" : "Web Desk"}
            </Typography>
            <Typography color="#676767" variant="body3" className="flex items-center gap-1 mt-1">
              Published on{" "}
              {moment(article?.createdAt).tz("Asia/Kolkata").format("MMM DD, YYYY, hh:mm A")} | {1}{" "}
              min read
            </Typography>
          </div>
        </div>
        <div className="">
          <SocialShare title={article?.title} url={`/podcasts/${article?.slug}`} />
        </div>
      </div>

      <div className="pb-8 w-full max-w-[610px] mx-auto">
        <LexicalRichText contentV2={article?.contentV2} />
      </div>
      <hr />

      <hr />

      {/* Tag Section */}
      {article?.tags?.length > 0 && (
        <div className="flex gap-5 mt-7">
          <Typography variant="subtitle2" className="uppercase">
            Tags
          </Typography>
          <div className="flex gap-5 overflow-x-scroll no-scrollbar">
            {article?.tags?.map((tag, index) => (
              <Link
                href={`/topics/tag/${tag?.slug}`}
                key={index}
                className="px-3 py-1 bg-[#FFF6DF] dark:bg-darkBgSecondaryColor rounded-full shadow-sm"
              >
                <Typography variant="body3" className="uppercase whitespace-nowrap">
                  {tag?.name}
                </Typography>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Whatsapp section */}
      <div className="mt-8">
        <WhatsappTelegramChannel language={lang === "en" ? "en" : "ml"} />
      </div>
    </div>
  );
};

export default PodcastDetailHeroSection;
